import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { FiAlertTriangle, FiInfo, FiX } from "react-icons/fi";
import { CALL_CENTER_MODAL_BACKDROP_CLASS } from "./callCenterModalStyles";

const VARIANT_CONFIG = {
  danger: {
    icon: FiAlertTriangle,
    iconWrap: "bg-red-50 text-red-600 ring-red-100",
    confirmButton:
      "bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-500/30 disabled:hover:bg-red-600",
  },
  warning: {
    icon: FiAlertTriangle,
    iconWrap: "bg-amber-50 text-amber-600 ring-amber-100",
    confirmButton:
      "bg-amber-600 text-white shadow-sm hover:bg-amber-700 focus-visible:ring-amber-500/30 disabled:hover:bg-amber-600",
  },
  primary: {
    icon: FiInfo,
    iconWrap: "bg-blue-50 text-[#2B7FFF] ring-blue-100",
    confirmButton:
      "bg-[#2B7FFF] text-white shadow-sm hover:bg-[#1d6ef2] focus-visible:ring-[#2B7FFF]/30 disabled:hover:bg-[#2B7FFF]",
  },
};

const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  processingLabel = "Processing...",
  variant = "danger",
  loading = false,
  onConfirm,
  onClose,
  overlayClassName = "z-[250]",
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const cancelRef = useRef(null);
  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.danger;
  const Icon = config.icon;

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => cancelRef.current?.focus(), 0);
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !loading) {
        onClose?.();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, loading, onClose]);

  if (!open || typeof document === "undefined") return null;

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && !loading) {
      onClose?.();
    }
  };

  return createPortal(
    <div
      className={`fixed inset-0 flex items-center justify-center p-4 ${CALL_CENTER_MODAL_BACKDROP_CLASS} ${overlayClassName}`}
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-[#f8fafc] shadow-2xl"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={cancelLabel}
        >
          <FiX className="h-5 w-5" />
        </button>

        <div className="px-6 pb-6 pt-8 text-center">
          <div
            className={`mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full ring-4 ${config.iconWrap}`}
          >
            <Icon className="h-6 w-6" aria-hidden="true" />
          </div>

          <h2 id={titleId} className="text-lg font-bold text-[#111827]">
            {title}
          </h2>
          <p id={descriptionId} className="mt-2 px-2 text-sm leading-relaxed text-slate-600">
            {message}
          </p>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/60 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`inline-flex h-10 min-w-[112px] items-center justify-center rounded-xl px-5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-70 ${config.confirmButton}`}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                {processingLabel}
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
